import { AgentConfig } from "../types";
import { VideoTools } from '../tools/VideoTools';
import { UniversalTools } from '../tools/UniversalTools';
import { AutonomousIntelligence } from '@/services/intelligence/AutonomousIntelligence';
import systemPrompt from '@agents/video/prompt.md?raw';
import { buildDomainRetrievalTools, buildDomainRetrievalDeclarations } from '../tools/DomainTools';
import { StorageTools } from '../tools/StorageTools';
import { VideoProjectTools } from '../tools/VideoProjectTools';
import { EditorTools } from '../tools/EditorTools';
import { BlenderTools } from '../tools/BlenderTools';
import { DirectorTools } from '../tools/DirectorTools';



const videoRetrievalConfig = {
    'videoJobs': { path: 'videoJobs', requiresUserIdFilter: true },
    'video_releases': { path: 'video_releases', requiresUserIdFilter: true },
    'generated_videos': { path: 'generated_videos', requiresUserIdFilter: true }
};
const videoRetrievalTools = buildDomainRetrievalTools('Video', videoRetrievalConfig);
const videoRetrievalDeclarations = buildDomainRetrievalDeclarations('Video', videoRetrievalConfig);

export const VideoAgent: AgentConfig = {
    id: 'video',
    name: 'Video Director',
    description: 'Specializes in video production, editing, and VFX.',
    color: 'bg-blue-600',
    category: 'department',
    systemPrompt: systemPrompt,
    get functions() {
        return {
            ...videoRetrievalTools,
            list_stored_assets: StorageTools.list_files,
            search_stored_assets: StorageTools.search_files,
            generate_video: VideoTools.generate_video,
            batch_edit_videos: VideoTools.batch_edit_videos,
            extend_video: VideoTools.extend_video,
            update_keyframe: VideoTools.update_keyframe,
            web_extract: UniversalTools.web_extract,
            indii_image_gen: UniversalTools.indii_image_gen,
            orchestrate_timeline: VideoTools.orchestrate_timeline,
            create_performance_video: VideoTools.create_performance_video,
            inspect_video_project: VideoProjectTools.inspect_video_project,
            add_video_clip: VideoProjectTools.add_video_clip,
            update_video_clip: VideoProjectTools.update_video_clip,
            queue_video_render: VideoProjectTools.queue_video_render,
            video_list_renderable_assets: EditorTools.video_list_renderable_assets,
            video_plan_sequence: EditorTools.video_plan_sequence,
            video_plan_chain: EditorTools.video_plan_chain,
            video_render_stitch: EditorTools.video_render_stitch,
            video_render_chain: EditorTools.video_render_chain,
            video_get_render_status: EditorTools.video_get_render_status,
            generate_storyboard: async (args: { script: string, numFrames: number }) => {
                const prompt = `Break down this script into a ${args.numFrames}-frame storyboard. For each frame, provide a shot type, action description, and visual prompt for image generation. Script: ${args.script}`;
                try {
                    const response = await AutonomousIntelligence.generateText(prompt, { maxOutputTokens: 8192, temperature: 1.0 });
                    return { success: true, data: { storyboard: response } };
                } catch (e: unknown) {
                    return { success: false, error: (e as Error).message };
                }
            },
            draft_video_budget: async (args: { durationMinutes: number, vfxLevel: string, locationDays: number }) => {
                const prompt = `Draft a video production budget for a ${args.durationMinutes} minute video with ${args.vfxLevel} VFX and ${args.locationDays} location shooting days. Include pre-pro, production, and post-production costs.`;
                try {
                    const response = await AutonomousIntelligence.generateText(prompt, { maxOutputTokens: 8192, temperature: 1.0 });
                    return { success: true, data: { budget: response } };
                } catch (e: unknown) {
                    return { success: false, error: (e as Error).message };
                }
            },
            analyze_audio: DirectorTools.analyze_audio,
            animate_still: VideoTools.animate_still,
            ...BlenderTools
        } as Record<string, import('@/services/agent/types').AnyToolFunction>;
    },
    authorizedTools: [
        'list_domain_records',
        'list_stored_assets',
        'search_stored_assets',
        'generate_video',
        'batch_edit_videos',
        'extend_video',
        'update_keyframe',
        'web_extract',
        'indii_image_gen',
        'orchestrate_timeline',
        'create_performance_video',
        'inspect_video_project',
        'add_video_clip',
        'update_video_clip',
        'queue_video_render',
        'generate_storyboard',
        'draft_video_budget',
        'video_list_renderable_assets',
        'video_plan_sequence',
        'video_plan_chain',
        'video_render_stitch',
        'video_render_chain',
        'video_get_render_status',
        'analyze_audio',
        'animate_still',
        'blender_get_status',
        'blender_list_templates',
        'blender_render_music_video',
        'blender_live_command'
    ],
    tools: [{
        functionDeclarations: [
            ...videoRetrievalDeclarations,
            {
                name: "list_stored_assets",
                description: "List the user’s saved gallery images, brand assets, reference images, and recent uploads. Use this to resolve requests like 'one of the last three images' before generating video.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        source: { type: "STRING", enum: ['gallery', 'brand_assets', 'reference_images', 'uploads', 'all'], description: "Asset source to list." },
                        limit: { type: "NUMBER", description: "Maximum number of assets to return." },
                        type: { type: "STRING", description: "Optional media type filter, such as image or video." }
                    },
                    required: []
                }
            },
            {
                name: "search_stored_assets",
                description: "Search the user’s saved gallery images, brand assets, reference images, and recent uploads by text.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        query: { type: "STRING", description: "Search words to match against prompts, type, or source." }
                    },
                    required: ['query']
                }
            },
            {
                name: "generate_video",
                description: "Generate a video from a text prompt or start image.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        prompt: { type: "STRING", description: "Description of motion/scene." },
                        image: { type: "STRING", description: "Optional base64 start image or HTTPS image URL." },
                        assetId: { type: "STRING", description: "Optional saved image asset ID to use as the first frame." },
                        recentImageIndex: { type: "NUMBER", description: "Optional zero-based index from the most recent generated/uploaded images to use as the first frame." },
                        duration: { type: "NUMBER", description: "Duration in seconds (4, 6, or 8 seconds; image-to-video requires 8 seconds)." }
                    },
                    required: ["prompt"]
                }
            },
            {
                name: "batch_edit_videos",
                description: "Edit/grade uploaded videos with an instruction.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        prompt: { type: "STRING", description: "Editing instruction." },
                        videoIndices: { type: "ARRAY", description: "Optional list of indices.", items: { type: "NUMBER" } }
                    },
                    required: ["prompt"]
                }
            },
            {
                name: "extend_video",
                description: "Extend a video clip forwards or backwards.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        videoUrl: { type: "STRING", description: "URL of the video to extend." },
                        prompt: { type: "STRING", description: "Content of the extension." },
                        direction: { type: "STRING", enum: ["start", "end"], description: "Direction to extend." }
                    },
                    required: ["videoUrl", "prompt", "direction"]
                }
            },
            {
                name: "update_keyframe",
                description: "Update animation keyframes.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        clipId: { type: "STRING", description: "ID of the clip." },
                        property: { type: "STRING", enum: ["scale", "opacity", "x", "y", "rotation"], description: "Property to animate." },
                        frame: { type: "NUMBER", description: "Frame number." },
                        value: { type: "NUMBER", description: "Value." },
                        easing: { type: "STRING", enum: ["linear", "easeIn", "easeOut", "easeInOut"], description: "Easing function." }
                    },
                    required: ["clipId", "property", "frame", "value"]
                }
            },
            {
                name: "web_extract",
                description: "Read bounded text from a public web page. This tool cannot log in, click, type, or submit forms.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        url: { type: "STRING", description: "Public HTTP(S) URL to read." }
                    },
                    required: ["url"]
                }
            },
            {
                name: "indii_image_gen",
                description: "Generate storyboard keyframes.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        prompt: { type: "STRING" },
                        aspect_ratio: { type: "STRING" }
                    },
                    required: ["prompt"]
                }
            },
            {
                name: "orchestrate_timeline",
                description: "Acts as a render supervisor, dynamically breaking down a master script/timeline into sequential 5-second descriptive prompts optimized for Veo generation.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        masterScript: { type: "STRING", description: "The overall vision or script for the video." },
                        totalDuration: { type: "NUMBER", description: "Total intended duration of the video in seconds." },
                        artStyle: { type: "STRING", description: "The overarching visual style to append to each prompt (e.g., 'Cinematic 35mm, neon noir')." }
                    },
                    required: ["masterScript", "totalDuration", "artStyle"]
                }
            },
            {
                name: "create_performance_video",
                description: "Create a multi-scene performance video aligned to a verified canonical master track with stylistic visual direction.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        masterAsset: { type: "OBJECT", description: "Verified canonical master audio reference." },
                        artistImageUrl: { type: "STRING", description: "Optional image URL of the artist for visual continuity." },
                        artistDescription: { type: "STRING", description: "Description of the artist's visual appearance and style." },
                        style: { type: "STRING", description: "Artistic/cinematic style directive." },
                        aspectRatio: { type: "STRING", enum: ["9:16", "16:9", "1:1"], description: "Target aspect ratio." },
                        sceneCount: { type: "NUMBER", description: "Number of performance scenes to generate." }
                    },
                    required: ["masterAsset"]
                }
            },
            {
                name: "analyze_audio",
                description: "Perform 'Audio-to-Visual' analysis to extract BPM, key, mood, transients, and energy from an uploaded track to guide video cutting and tempo sync.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        uploadedAudioIndex: { type: "NUMBER", description: "Optional index of a recently uploaded audio file." }
                    },
                    required: []
                }
            },
            {
                name: "animate_still",
                description: "Render a deterministic camera move (dolly-in, pan, tilt, ken-burns) over a still image into a short promotional video clip. Free, no generative AI tokens.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        imageUrl: { type: "STRING", description: "URL of the still image." },
                        imageIndex: { type: "NUMBER", description: "Index of image if URL is omitted." },
                        preset: { type: "STRING", enum: ["dolly-in", "dolly-out", "pan-left", "pan-right", "tilt-up", "tilt-down", "ken-burns"], description: "Camera move preset." },
                        resolution: { type: "STRING", enum: ["9:16", "16:9", "1:1"], description: "Aspect ratio." },
                        durationSeconds: { type: "NUMBER", description: "Clip duration in seconds (default 3.0)." }
                    },
                    required: []
                }
            },
            {
                name: "inspect_video_project",
                description: "Inspect the active indii video project, including its tracks, clips, timing, layout, transitions, and keyframes. Call this before editing the timeline.",
                parameters: { type: "OBJECT", properties: {}, required: [] }
            },
            {
                name: "add_video_clip",
                description: "Add a video, image, text, or audio clip to the active editor timeline. The live HyperFrames preview updates automatically.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        type: { type: "STRING", enum: ["video", "image", "text", "audio"] },
                        name: { type: "STRING" },
                        trackId: { type: "STRING", description: "Optional compatible track ID; otherwise the first compatible track is used." },
                        src: { type: "STRING", description: "Media URL for video, image, or audio." },
                        text: { type: "STRING", description: "Text content for a text clip." },
                        startFrame: { type: "NUMBER" },
                        durationInFrames: { type: "NUMBER" },
                        x: { type: "NUMBER" }, y: { type: "NUMBER" }, width: { type: "NUMBER" }, height: { type: "NUMBER" },
                        opacity: { type: "NUMBER" }, rotation: { type: "NUMBER" }, volume: { type: "NUMBER" },
                        textColor: { type: "STRING" }, fontSize: { type: "NUMBER" },
                        textAlign: { type: "STRING", enum: ["left", "center", "right"] }
                    },
                    required: ["type", "name", "startFrame", "durationInFrames"]
                }
            },
            {
                name: "update_video_clip",
                description: "Update an existing clip on the active timeline. Inspect the project first to get the clip ID; omitted properties remain unchanged.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        clipId: { type: "STRING" }, name: { type: "STRING" }, src: { type: "STRING" }, text: { type: "STRING" },
                        startFrame: { type: "NUMBER" }, durationInFrames: { type: "NUMBER" },
                        x: { type: "NUMBER" }, y: { type: "NUMBER" }, width: { type: "NUMBER" }, height: { type: "NUMBER" },
                        opacity: { type: "NUMBER" }, rotation: { type: "NUMBER" }, volume: { type: "NUMBER" },
                        textColor: { type: "STRING" }, fontSize: { type: "NUMBER" },
                        textAlign: { type: "STRING", enum: ["left", "center", "right"] }
                    },
                    required: ["clipId"]
                }
            },
            {
                name: "queue_video_render",
                description: "Render the active video-editor project through indii's planner. Direct media uses FFmpeg; composed timelines use HyperFrames. The completed MP4 becomes the editor preview artifact.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        projectId: { type: "STRING", description: "Optional safety check against the active video project." },
                        outputName: { type: "STRING", description: "Optional MP4 filename." }
                    },
                    required: []
                }
            },
            {
                name: "generate_storyboard",
                description: "Break down a script into a storyboard with visual prompts.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        script: { type: "STRING" },
                        numFrames: { type: "NUMBER" }
                    },
                    required: ["script", "numFrames"]
                }
            },
            {
                name: "draft_video_budget",
                description: "Draft a video production budget.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        durationMinutes: { type: "NUMBER" },
                        vfxLevel: { type: "STRING", enum: ["low", "medium", "high"] },
                        locationDays: { type: "NUMBER" }
                    },
                    required: ["durationMinutes", "vfxLevel", "locationDays"]
                }
            },
            {
                name: 'video_list_renderable_assets',
                description: "List the user's finished video assets with duration, aspect ratio, and download URLs. Use before planning sequences.",
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        aspectRatio: { type: 'STRING', enum: ['16:9', '9:16'], description: 'Filter by aspect ratio.' },
                        minDurationSeconds: { type: 'NUMBER', description: 'Filter by minimum duration in seconds.' }
                    }
                }
            },
            {
                name: 'video_plan_sequence',
                description: 'Plan a beat-snapped video timeline sequence from existing finished assets without rendering or spending credits.',
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        assetIds: { type: 'ARRAY', items: { type: 'STRING' }, description: 'Array of at least 2 asset IDs in desired order.' },
                        bpm: { type: 'NUMBER', description: 'Music BPM for beat snapping (default 120).' },
                        beatSnapped: { type: 'BOOLEAN', description: 'Whether to snap cuts to nearest beat (default true).' },
                        aspectRatio: { type: 'STRING', enum: ['16:9', '9:16'], description: 'Aspect ratio of sequence.' },
                        transitionDurationSeconds: { type: 'NUMBER', description: 'Transition overlap in seconds (default 1.0).' }
                    },
                    required: ['assetIds']
                }
            },
            {
                name: 'video_plan_chain',
                description: 'Plan a sequential beat-snapped video chain from existing assets.',
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        assetIds: { type: 'ARRAY', items: { type: 'STRING' }, description: 'Array of at least 2 asset IDs.' },
                        bpm: { type: 'NUMBER', description: 'Music BPM for beat snapping (default 120).' },
                        aspectRatio: { type: 'STRING', enum: ['16:9', '9:16'], description: 'Aspect ratio.' }
                    },
                    required: ['assetIds']
                }
            },
            {
                name: 'video_render_stitch',
                description: 'Submit a billable multi-segment video stitch render for an approved sequence plan. Requires explicit user approval and server cost reservation.',
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        planId: { type: 'STRING', description: 'Plan ID returned from video_plan_sequence.' },
                        assetIds: { type: 'ARRAY', items: { type: 'STRING' }, description: 'Optional asset IDs if planId is omitted.' },
                        projectId: { type: 'STRING', description: 'Associated project ID.' },
                        aspectRatio: { type: 'STRING', enum: ['16:9', '9:16'], description: 'Target aspect ratio.' }
                    }
                }
            },
            {
                name: 'video_render_chain',
                description: 'Submit a billable sequential video chain render. Requires explicit user approval and server cost reservation.',
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        planId: { type: 'STRING', description: 'Plan ID from video_plan_chain.' },
                        assetIds: { type: 'ARRAY', items: { type: 'STRING' }, description: 'Asset IDs if planId omitted.' }
                    }
                }
            },
            {
                name: 'video_get_render_status',
                description: 'Check the real-time status of a video render job (queued, rendering, succeeded, failed) and retrieve the final video URL.',
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        renderId: { type: 'STRING', description: 'Render ID returned by video_render_stitch.' }
                    },
                    required: ['renderId']
                }
            },
            {
                name: 'blender_get_status',
                description: 'Check Blender 3D integration status, binary installation path, version, and GPU acceleration capabilities on the host system.',
                parameters: {
                    type: 'OBJECT',
                    properties: {},
                    required: []
                }
            },
            {
                name: 'blender_list_templates',
                description: 'List available procedural 3D music video templates (e.g. audio-reactive tunnels, spectrum analyzers, particles, vinyl turntables).',
                parameters: {
                    type: 'OBJECT',
                    properties: {},
                    required: []
                }
            },
            {
                name: 'blender_render_music_video',
                description: 'Render a procedural 3D music video or audio-reactive visualizer using Blender EEVEE/Cycles.',
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        audioFilePath: { type: 'STRING', description: 'Absolute path to the input audio file (WAV/MP3/AAC).' },
                        outputVideoPath: { type: 'STRING', description: 'Absolute destination path for the rendered MP4 video.' },
                        templateId: { type: 'STRING', description: 'Template identifier: audio_reactive_tunnel, frequency_spectrum_bars, particle_nebula, neon_grid_horizon, vinyl_turntable, abstract_geometry_morph.' },
                        bpm: { type: 'NUMBER', description: 'Optional tempo in BPM to sync visual pulsations and camera motion.' },
                        durationSeconds: { type: 'NUMBER', description: 'Duration of the output render in seconds (default 30).' },
                        fps: { type: 'NUMBER', description: 'Frames per second (default 30).' },
                        resolution: { type: 'STRING', enum: ['720p', '1080p', '4k'], description: 'Render resolution (default 1080p).' },
                        aspectRatio: { type: 'STRING', enum: ['16:9', '9:16', '1:1'], description: 'Aspect ratio (default 16:9).' },
                        engine: { type: 'STRING', enum: ['BLENDER_EEVEE_NEXT', 'CYCLES'], description: 'Blender render engine (default BLENDER_EEVEE_NEXT).' }
                    },
                    required: ['audioFilePath', 'outputVideoPath']
                }
            },
            {
                name: 'blender_live_command',
                description: 'Execute a command against a live running Blender instance via the Blender MCP server bridge.',
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        action: { type: 'STRING', enum: ['ping', 'get_scene_info', 'set_frame'], description: 'Declarative scene command. Python execution is not supported.' },
                        params: { type: 'OBJECT', description: 'Parameters for the action.' }
                    },
                    required: ['action']
                }
            }
        ]
    }]
};

// Freeze the schema to prevent cross-test contamination
